/**
 * The placing "book": everything the placing views show, derived from the
 * raw PlacingAPI records. Views (broker portal, carrier inbox, deal) render
 * from this, so new API data (e.g. the Order process) only changes here.
 *
 * Order and Bound are read from line requests, offers (written lines) and
 * signed line advices where those records exist; there are no workflow
 * endpoints for them yet.
 */
import { LINE_ROLES, eligibleForQuoteRequest, furthestRequest, isInterested, isPast, readOffer, readParticipation, requestCounts } from "./placingUtils";

export const STAGES = ["submission", "quotation", "order", "bound"];
/** The same four stages by the names the screens use. */
export const STAGE_KEYS = { submission: "build", quotation: "quotation", order: "order", bound: "signing" };
// Shown as Build, Quotation, Order and Signing (the keys stay as they were)
export const STAGE_LABELS = { submission: "Build", quotation: "Quotation", order: "Order", bound: "Signing" };
const STAGE_CODE_INDEX = { build: 0, submission: 0, quotation: 1, order: 2, bound: 3 };

const raw = (value) => value?.$date ?? value;
const time = (value) => (raw(value) ? new Date(raw(value)).getTime() : 0);
const sum = (values) => values.reduce((total, v) => total + (Number(v) || 0), 0);
const round = (n) => Math.round(n * 100) / 100;
const groupBy = (list, key) =>
  (list || []).reduce((map, item) => {
    const k = key(item);
    if (k) (map[k] = map[k] || []).push(item);
    return map;
  }, {});

// The carrier party on each kind of record
const carrierOf = {
  quoteRequest: (r) => r.receiver?.partyId,
  lineRequest: (r) => r.receiver?.partyId,
  signedLineAdvice: (r) => r.receiver?.partyId,
  quote: (r) => r.sender?.partyId,
  offer: (r) => r.sender?.partyId,
};

const isOpenRequest = (q) => q.status === "sent";
const isOverdueRequest = (q) => isOpenRequest(q) && isPast(q.responseRequiredByDate);

const newest = (list) => [...(list || [])].sort((a, b) => time(b.stampedDate || b.createdDate) - time(a.stampedDate || a.createdDate))[0];

/** Each carrier's latest offer (a carrier can send several lines). */
const latestOfferPerCarrier = (offers) =>
  Object.values(groupBy(offers, (o) => o.sender?.partyId)).map(newest);

/** Groups every record type by placement. */
export const indexRecords = (records) => {
  const by = (list) => groupBy(list, (r) => r.placementId);
  return {
    submissions: by(records.submissions),
    quoteRequests: by(records.quoteRequests),
    quotes: by(records.quotes),
    lineRequests: by(records.lineRequests),
    offers: by(records.offers),
    signedLineAdvices: by(records.signedLineAdvices),
  };
};

/**
 * The stage to show for a placement or submission: build until a quote
 * request has been sent, then quotation; order and bound come from the code.
 */
export const displayStageCode = (code, hasQuoteRequests) => {
  const index = STAGE_CODE_INDEX[code] ?? 0;
  if (index >= 2) return code;
  return hasQuoteRequests ? "quotation" : "build";
};

/**
 * One placement's position: { stageIndex, stage, stageSummary, market,
 * next: { label, tone }, needsYou, due, updated, ... }. tone is "action"
 * (needs you), "waiting", "done" or "info".
 */
export const summarisePlacement = (placement, index) => {
  const id = placement._id;
  const submission = (index.submissions[id] || [])[0] || null;
  const leads = submission?.sharedWithParties || [];
  const quoteRequests = index.quoteRequests[id] || [];
  const quotes = index.quotes[id] || [];
  const lineRequests = index.lineRequests[id] || [];
  const offers = index.offers[id] || [];
  const signedLineAdvices = index.signedLineAdvices[id] || [];

  const isShared = submission?.status === "shared";
  const quoted = quoteRequests.filter((q) => ["quoted", "nominated"].includes(q.status)).length;
  const declined = quoteRequests.filter((q) => q.status === "declined").length;
  const nominated = quoteRequests.some((q) => q.status === "nominated");
  const awaiting = quoteRequests.filter(isOpenRequest).length;
  const overdue = quoteRequests.filter(isOverdueRequest).length;
  const sentLeads = leads.filter((l) => l.status !== "selected");
  // Any interested lead can be sent a quote request (even another one); the
  // nudge is for interested leads that haven't been sent one yet
  const sentCounts = requestCounts(quoteRequests);
  const eligible = eligibleForQuoteRequest(leads).filter((l) => !sentCounts[l.partyId]).length;
  const interested = leads.filter(isInterested).length;

  // A placement stays in build (Submission) when its submission is shared and
  // moves to Quotation only once a quote request is sent; so Quotation comes
  // from the quote requests, and the stage code only decides Order and Bound.
  // Signing down (signed line advices, placement status signed) is the last
  // step of Order, shown as the Signing stage (as does a bound stage code)
  const evidence = offers.length || lineRequests.length || signedLineAdvices.length ? 2 : quoteRequests.length ? 1 : 0;
  const signedDown = placement.status === "signed" || signedLineAdvices.length > 0;
  const codeIndex = STAGE_CODE_INDEX[placement.placingStageCode] ?? 0;
  const stageIndex = signedDown ? 3 : Math.max(evidence, codeIndex >= 2 ? codeIndex : 0);
  const stage = STAGES[stageIndex];

  // Written so far: each carrier's latest line only (an indication; signing down comes later)
  const written = round(sum(latestOfferPerCarrier(offers).map((o) => readOffer(o).writtenShare)));
  const signed = round(sum(signedLineAdvices.map((s) => readParticipation(s).signedShare)));
  const market = signedLineAdvices.length
    ? { percent: signed, label: "signed" }
    : offers.length
    ? { percent: written, label: "written" }
    : null;

  let stageSummary;
  if (signedDown) stageSummary = `signed down · ${signedLineAdvices.length} advice(s)`;
  else if (stageIndex === 3) stageSummary = `signed ${signed}% · ${signedLineAdvices.length} advice(s)`;
  else if (stageIndex === 2)
    stageSummary = `${lineRequests.filter((r) => r.status === "offered").length} of ${lineRequests.length || offers.length} written`;
  else if (stageIndex === 1)
    stageSummary = `${quoted} of ${quoteRequests.length} quoted${declined ? ` · ${declined} declined` : ""}${nominated ? " · lead nominated" : ""}`;
  else if (!submission) stageSummary = "not started";
  else if (isShared)
    stageSummary = `sent to ${sentLeads.length} · ${interested ? `${interested} interested` : `${leads.filter((l) => l.viewedDate).length} viewed`}`;
  else stageSummary = `${leads.length} lead(s) · ${leads.filter((l) => l.viewedDate).length} viewed`;

  let next;
  if (signedDown) next = { label: "Done — signed down", tone: "done" };
  else if (offers.length || lineRequests.length) {
    const linesAwaiting = lineRequests.filter((r) => r.status === "sent");
    const linesOverdue = linesAwaiting.filter((r) => isPast(r.responseRequiredByDate)).length;
    next = linesOverdue
      ? { label: `Chase ${linesOverdue} carrier(s)`, tone: "action" }
      : linesAwaiting.length
      ? { label: `Awaiting ${linesAwaiting.length} line(s)`, tone: "waiting" }
      : { label: "Order in progress", tone: "info" };
  }
  else if (!submission) next = { label: "Create submission", tone: "action" };
  else if (!isShared)
    next = leads.some((l) => l.status === "selected")
      ? { label: "Send submission", tone: "action" }
      : { label: "Choose leads", tone: "action" };
  else if (nominated) next = { label: "Lead nominated", tone: "done" };
  else if (quoted) next = { label: "Nominate lead", tone: "action" };
  else if (eligible) next = { label: `Send ${eligible} quote request(s)`, tone: "action" };
  else if (overdue) next = { label: `Chase ${overdue} carrier(s)`, tone: "action" };
  else if (awaiting) next = { label: `Awaiting ${awaiting} quote(s)`, tone: "waiting" };
  else if (sentLeads.length && sentLeads.every((l) => l.status === "declined" || l.responseCode === "decline_to_participate"))
    next = { label: "Add potential leads", tone: "action" };
  else next = { label: "Awaiting interest", tone: "waiting" };

  const openDates = quoteRequests.filter(isOpenRequest).map((q) => raw(q.responseRequiredByDate)).filter(Boolean);
  const due = openDates.length
    ? openDates.sort((a, b) => time(a) - time(b))[0]
    : isShared && !quoteRequests.length
    ? raw(submission.responseRequiredByDate) || null
    : null;

  // The latest thing a carrier did (responded, quoted, declined, wrote a line):
  // what makes a placement "new" for the broker
  const carrierUpdated = [
    ...leads.flatMap((l) => [l.respondedDate, l.declinedDate]),
    ...quoteRequests.map((q) => q.receiver?.declinedDate),
    ...quotes.map((q) => q.stampedDate || q.createdDate),
    ...lineRequests.map((r) => r.receiver?.declinedDate),
    ...offers.map((o) => o.stampedDate || o.createdDate),
  ]
    .map(raw)
    .filter(Boolean)
    .sort((a, b) => time(b) - time(a))[0] || null;

  const updated = [placement, submission, ...quoteRequests, ...quotes, ...offers, ...signedLineAdvices]
    .filter(Boolean)
    .map((r) => raw(r.updatedDate) || raw(r.createdDate))
    .filter(Boolean)
    .sort((a, b) => time(b) - time(a))[0] || null;

  return {
    placement,
    submission,
    leads,
    quoteRequests,
    quotes,
    lineRequests,
    offers,
    signedLineAdvices,
    stageIndex,
    stage,
    stageKey: STAGE_KEYS[stage],
    carrierUpdated,
    stageSummary,
    market,
    next,
    needsYou: next.tone === "action",
    signedDown,
    isBound: signedDown || stageIndex === 3,
    due,
    updated,
  };
};

/**
 * Each carrier's position on one placement, for the market grid:
 * { text, tone, isLead, lineToStand } or null. tone is "signed", "written",
 * "quoted", "requested", "declined" or "muted".
 */
export const marketCell = (summary, partyId) => {
  const mine = (list, kind) => list.filter((r) => carrierOf[kind](r) === partyId);
  const sla = mine(summary.signedLineAdvices, "signedLineAdvice")[0];
  const offer = newest(mine(summary.offers, "offer"));
  const quotes = mine(summary.quotes, "quote");
  // A carrier can have several quote requests: show its furthest-along one
  const requests = mine(summary.quoteRequests, "quoteRequest");
  const request = furthestRequest(requests);
  const moreRequests = Math.max(0, requests.length - 1);
  const lead = summary.leads.find((l) => l.partyId === partyId);
  const isLead = request?.status === "nominated" || quotes.some((q) => q.status === "nominated");

  if (sla) return { text: `${readParticipation(sla).signedShare ?? "—"}% signed`, tone: "signed", isLead };
  if (offer) {
    const line = readOffer(offer);
    return {
      text: `${line.writtenShare ?? "—"}% written${line.lineType === "conditional_line" ? " · conditional" : ""}`,
      tone: "written",
      isLead,
      lineToStand: line.lineToStand,
    };
  }
  // The carrier's quote now: nominated, else its newest live quote, else its
  // newest (an earlier quote superseded by a later one isn't shown)
  const live = quotes.filter((q) => !q.expired && (q.status == null || q.status === "sent"));
  const quote = quotes.find((q) => q.status === "nominated") || newest(live) || newest(quotes);
  if (quote) {
    const p = readParticipation(quote);
    const range = p.minimumShare != null || p.maximumShare != null ? ` (${p.minimumShare ?? "—"}–${p.maximumShare ?? "—"})` : "";
    const notTakenUp = quote.status === "ntu" || (quote.status !== "nominated" && request?.status === "ntu");
    const word = quote.status === "nominated" ? "nominated" : notTakenUp ? "quoted · NTU" : "quoted";
    return {
      text: p.quotedShare != null ? `${p.quotedShare}% ${word}${range}` : `${word}${range}`,
      tone: notTakenUp ? "muted" : "quoted",
      isLead,
      moreRequests,
    };
  }
  if (request?.status === "declined") return { text: "Declined to quote", tone: "declined", moreRequests };
  if (request) {
    const requested = readParticipation(request).requestedShare;
    const dueDate = raw(request.responseRequiredByDate);
    const due = dueDate ? ` · due ${new Date(dueDate).toLocaleDateString(undefined, { day: "numeric", month: "short" })}` : "";
    return { text: `${requested != null ? `${requested}% ` : ""}requested${due}`, tone: isOverdueRequest(request) ? "declined" : "requested", moreRequests };
  }
  if (lead?.status === "declined") return { text: "Declined", tone: "declined" };
  if (lead && isInterested(lead)) return { text: "Interested", tone: "interested" };
  if (lead) return { text: lead.status === "selected" ? "Potential lead" : "Submission sent", tone: "muted" };
  return null;
};

/** Every carrier with a position on any of the placements. */
export const marketParties = (summaries) => [
  ...new Set(
    summaries.flatMap((s) => [
      ...s.leads.map((l) => l.partyId),
      ...s.quoteRequests.map(carrierOf.quoteRequest),
      ...s.offers.map(carrierOf.offer),
      ...s.signedLineAdvices.map(carrierOf.signedLineAdvice),
    ])
  ),
].filter(Boolean);

/** The total line for a placement's market column. */
export const marketTotal = (summary) => {
  if (summary.signedLineAdvices.length) return `${summary.market.percent}% signed`;
  if (summary.offers.length) return `${summary.market.percent}% written of 100%`;
  const quotes = summary.quoteRequests.filter((q) => ["quoted", "nominated"].includes(q.status)).length;
  const declined = summary.quoteRequests.filter((q) => q.status === "declined").length;
  return summary.quoteRequests.length ? `${quotes} quote(s) · ${declined} declined` : "—";
};

/**
 * Recent activity worked out from the records' dates (there's no activity
 * feed in the API): [{ date, partyId, describe(partyName) }], newest first.
 */
export const recentActivity = (summaries, limit = 8) => {
  const events = [];
  const add = (date, partyId, describe) => raw(date) && events.push({ date: raw(date), partyId, describe });
  summaries.forEach((s) => {
    const name = s.placement.name || "a placement";
    s.leads.forEach((l) => {
      add(l.sentDate, l.partyId, (p) => `${name} submission sent to ${p}`);
      add(l.viewedDate, l.partyId, (p) => `${name} submission viewed by ${p}`);
      if (l.responseCode === "declare_interest") add(l.respondedDate, l.partyId, (p) => `${p} declared interest in ${name}`);
      else add(l.respondedDate || l.declinedDate, l.partyId, (p) => `${p} declined the ${name} submission`);
    });
    s.quoteRequests.forEach((q) => {
      add(q.createdDate, q.receiver?.partyId, (p) => `Quote request sent to ${p} on ${name}`);
      add(q.receiver?.declinedDate, q.receiver?.partyId, (p) => `${p} declined to quote on ${name}`);
    });
    s.quotes.forEach((q) => {
      const share = readParticipation(q).quotedShare;
      add(q.stampedDate || q.createdDate, q.sender?.partyId, (p) => `${p} quoted ${share != null ? `${share}% ` : ""}on ${name}`);
      if (q.status === "nominated") add(q.decidedDate, q.sender?.partyId, (p) => `${p} nominated as lead on ${name}`);
    });
    s.offers.forEach((o) => {
      const p0 = readParticipation(o);
      add(o.createdDate, o.sender?.partyId, (p) => `${p} wrote ${p0.writtenShare ?? "—"}%${p0.lineToStandIndicator ? " (to stand)" : ""} on ${name}`);
    });
    s.signedLineAdvices.forEach((a) =>
      add(a.createdDate, a.receiver?.partyId, (p) => `${p} signed ${readParticipation(a).signedShare ?? "—"}% on ${name}`)
    );
  });
  return events.sort((a, b) => time(b.date) - time(a.date)).slice(0, limit);
};

/**
 * The carrier's party entries on a submission they were sent. GET
 * /submissions/{id} returns only the entries for parties the user acts for
 * (one user can act for several carriers, each with its own status and
 * dates). The submissions list may leave them out; then the parties come
 * from the quote requests sent on it, or a single entry with no known party.
 * Never fetched by id here: that would record the carrier as having viewed it.
 */
const myEntries = (submission, receivedRequests = []) => {
  if (submission?.sharedWithParties?.length) return submission.sharedWithParties;
  const fromRequests = [
    ...new Set(receivedRequests.filter((q) => q.submissionId === submission?._id).map((q) => q.receiver?.partyId).filter(Boolean)),
  ].map((partyId) => ({ partyId, status: "sent" }));
  return fromRequests.length ? fromRequests : [{ partyId: null, status: "unknown" }];
};

/**
 * A carrier's inbox: one row per thing sent to one of their parties, or sent
 * back by one of them. A user acting for several carriers gets a row per
 * party (asPartyId), and each row's status comes from that party's record.
 * `mine(record)` says whether one of the user's organisations owns it.
 * Rows: { key, type, received, riskName, fromPartyId, asPartyId, role, asked,
 * respondBy, tab ("needs" | "waiting" | "signed" | "other"), link, actionLabel }.
 */
export const carrierInbox = (records, mine) => {
  const submissionsById = Object.fromEntries((records.submissions || []).map((s) => [s._id, s]));
  const quotesByRequest = groupBy(records.quotes, (q) => q.quoteRequestId);
  const received = (list) => (list || []).filter((r) => !mine(r));
  // Quote requests sent to a party on a submission, keyed "submissionId|partyId"
  const requestFor = groupBy(received(records.quoteRequests), (q) => `${q.submissionId}|${q.receiver?.partyId}`);
  const riskOf = (r) => submissionsById[r.submissionId]?.name || r.name || "Risk";
  // The contract document a message is about (its own, else its submission's)
  const documentOf = (r) => r.documentId || submissionsById[r.submissionId]?.documentId || null;
  const shareText = (p, verb) =>
    p.minimumShare != null && p.maximumShare != null
      ? `${verb} ${p.minimumShare}–${p.maximumShare}%`
      : p.requestedShare != null
      ? `${verb} ${p.requestedShare}%`
      : verb;
  const rows = [];

  // Several quote requests can go to the same party on one submission
  const requestNumber = {};
  Object.values(requestFor).forEach((list) =>
    [...list]
      .sort((a, b) => time(a.createdDate) - time(b.createdDate))
      .forEach((q, i) => (requestNumber[q._id] = { index: i + 1, of: list.length }))
  );

  received(records.quoteRequests).forEach((q) => {
    const p = readParticipation(q);
    const myQuote = (quotesByRequest[q._id] || [])[0];
    const qp = readParticipation(myQuote);
    const type = { sent: "Quote request", quoted: "Quote sent", nominated: "Nominated", ntu: "Not taken up", declined: "Declined" }[q.status] || "Quote request";
    rows.push({
      key: `qr-${q._id}`,
      type,
      // Why it ended: the carrier's decline (B22 on its entry) or the broker's NTU
      reason: q.status === "declined"
        ? { code: q.receiver?.reasonCode, notes: q.receiver?.declineReason }
        : q.status === "ntu"
        ? { code: q.ntuReasonCode, notes: q.ntuNotes, ntu: true }
        : null,
      received: raw(q.createdDate),
      sentDate: raw(q.receiver?.sentDate) || raw(q.createdDate),
      requestedShare: p.requestedShare,
      requestNumber: requestNumber[q._id],
      riskName: riskOf(q),
      documentId: documentOf(q),
      fromPartyId: q.sender?.partyId,
      asPartyId: q.receiver?.partyId,
      role: q.status === "nominated" ? "Lead" : "Potential lead",
      asked:
        q.status === "sent"
          ? shareText(p, "Quote")
          : myQuote
          ? `You quoted ${qp.quotedShare ?? "—"}%${qp.minimumShare != null ? ` (${qp.minimumShare}–${qp.maximumShare})` : ""}`
          : q.status === "declined"
          ? "You declined to quote"
          : shareText(p, "Quote"),
      respondBy: q.status === "sent" ? raw(q.responseRequiredByDate) : myQuote ? raw(myQuote.validUntilDate) : null,
      respondByLabel: q.status === "sent" ? null : myQuote ? "valid to" : null,
      overdue: isOverdueRequest(q),
      tab: q.status === "sent" ? "needs" : q.status === "quoted" ? "waiting" : "other",
      stage: "quotation",
      // Sent, or later nominated / not taken up by the broker
      newAt: ["nominated", "ntu"].includes(q.status) ? raw(q.updatedDate) || raw(q.createdDate) : raw(q.receiver?.sentDate) || raw(q.createdDate),
      link: `/placing/quoterequests/${q._id}`,
      actionLabel: q.status === "sent" ? "Respond" : "Open",
    });
  });

  received(records.submissions).forEach((s) => {
    myEntries(s, received(records.quoteRequests)).forEach((entry) => {
      // Once this party has a quote request, that row stands for it
      if ((requestFor[`${s._id}|${entry.partyId}`] || []).length) return;
      const declined = entry.status === "declined" || entry.responseCode === "decline_to_participate";
      const interested = isInterested(entry);
      const unknown = entry.status === "unknown";
      rows.push({
        key: `sub-${s._id}-${entry.partyId}`,
        type: "Submission",
        reason: declined ? { code: entry.reasonCode, notes: entry.responseNotes || entry.declineReason } : null,
        received: raw(entry.sentDate) || raw(s.createdDate),
        riskName: s.name || "Risk",
        documentId: s.documentId || null,
        fromPartyId: s.sender?.partyId,
        asPartyId: entry.partyId,
        role: "Potential lead",
        asked: declined
          ? "You declined to participate"
          : interested
          ? "You declared interest"
          : unknown
          ? "Open to see your response"
          : "Declare interest or decline",
        respondBy: declined || interested || unknown ? null : raw(s.responseRequiredByDate),
        // Responding at build is asked of them; once interested they wait on the broker
        tab: declined || unknown ? "other" : interested ? "waiting" : "needs",
        isOpenSubmission: !declined && !unknown,
        stage: "build",
        newAt: raw(entry.sentDate) || raw(s.createdDate),
        link: `/placing/submissions/${s._id}`,
        actionLabel: declined || interested || unknown ? "Open" : "Respond",
      });
    });
  });

  // One row per line request (a carrier can be sent several), with its latest line
  const offersByLineRequest = groupBy((records.offers || []).filter(mine), (o) => o.lineRequestId);
  received(records.lineRequests).forEach((r) => {
    const latest = newest(offersByLineRequest[r._id]);
    const line = readOffer(latest);
    const role = r.receiver?.roleCode;
    rows.push({
      key: `lr-${r._id}`,
      type: "Line request",
      reason: r.status === "declined"
        ? { code: r.receiver?.reasonCode, notes: r.receiver?.declineReason }
        : r.status === "ntu"
        ? { code: r.ntuReasonCode, notes: r.ntuNotes, ntu: true }
        : null,
      received: raw(r.createdDate),
      sentDate: raw(r.receiver?.sentDate) || raw(r.createdDate),
      requestedShare: readParticipation(r).requestedShare,
      riskName: riskOf(r),
      documentId: documentOf(r),
      fromPartyId: r.sender?.partyId,
      asPartyId: r.receiver?.partyId,
      lineRole: role,
      role: LINE_ROLES[role]?.label || "Follower",
      asked: latest
        ? `You wrote ${line.writtenShare ?? "—"}%${line.lineType === "conditional_line" ? " · conditional" : ""}${line.lineToStand ? " · to stand" : ""}`
        : r.status === "declined"
        ? "You declined"
        : r.status === "ntu"
        ? "Not taken up"
        : shareText(readParticipation(r), "Line of"),
      respondBy: r.status === "sent" ? raw(r.responseRequiredByDate) : null,
      overdue: r.status === "sent" && isPast(r.responseRequiredByDate),
      status: r.status,
      tab: r.status === "sent" ? "needs" : r.status === "offered" ? "waiting" : "other",
      stage: "order",
      newAt: r.status === "ntu" ? raw(r.updatedDate) || raw(r.createdDate) : raw(r.receiver?.sentDate) || raw(r.createdDate),
      link: `/placing/linerequests/${r._id}`,
      actionLabel: r.status === "sent" ? "Respond" : "Open",
    });
  });

  // The carrier's confirmed line: one row per signed line advice
  received(records.signedLineAdvices).forEach((a) => {
    const p = readParticipation(a);
    rows.push({
      key: `sla-${a._id}`,
      type: "Signed line advice",
      received: raw(a.receiver?.sentDate) || raw(a.createdDate),
      riskName: riskOf(a),
      documentId: documentOf(a),
      fromPartyId: a.sender?.partyId,
      asPartyId: a.receiver?.partyId,
      lineRole: a.receiver?.roleCode,
      role: LINE_ROLES[a.receiver?.roleCode]?.label || "Follower",
      asked: `Written ${p.writtenShare ?? "—"}% · signed ${p.signedShare ?? "—"}%${p.lineToStandIndicator ? " (stood)" : ""}`,
      respondBy: null,
      tab: "signed",
      stage: "signing",
      newAt: raw(a.receiver?.sentDate) || raw(a.createdDate),
      link: `/placing/signedlineadvices/${a._id}`,
      actionLabel: "Open",
    });
  });

  return rows.sort((a, b) => time(b.received) - time(a.received));
};

/**
 * The carrier's own lines: one per risk per party they act for, with that
 * party's quoted / written / signed shares and where it has got to. Never
 * other carriers' lines or market totals.
 */
export const carrierLines = (records, mine) => {
  const forParty = (list, submissionId, partyOf, partyId) =>
    (list || []).find((r) => r.submissionId === submissionId && partyOf(r) === partyId);

  const receivedLineRequests = (records.lineRequests || []).filter((r) => !mine(r));
  const offersByLineRequest = groupBy((records.offers || []).filter(mine), (o) => o.lineRequestId);
  const submissionsById = Object.fromEntries((records.submissions || []).map((s) => [s._id, s]));
  const hasLineRequest = (submissionId, partyId) =>
    receivedLineRequests.some((r) => r.submissionId === submissionId && carrierOf.lineRequest(r) === partyId);

  // Order: one line per line request, with its latest line (followers may
  // never have been sent the submission)
  const orderLines = receivedLineRequests.map((r) => {
    const latest = newest(offersByLineRequest[r._id]);
    const line = readOffer(latest);
    const sla = (records.signedLineAdvices || []).find(
      (a) =>
        !mine(a) &&
        (a.lineRequestId
          ? a.lineRequestId === r._id
          : a.submissionId === r.submissionId && carrierOf.signedLineAdvice(a) === carrierOf.lineRequest(r))
    );
    return {
      key: `lr-${r._id}`,
      riskName: submissionsById[r.submissionId]?.name || r.name || "Risk",
      asPartyId: carrierOf.lineRequest(r),
      link: `/placing/linerequests/${r._id}`,
      role: LINE_ROLES[r.receiver?.roleCode]?.label || "Follower",
      requested: readParticipation(r).requestedShare,
      written: line.writtenShare,
      lineType: latest ? line.lineType : null,
      signed: readParticipation(sla).signedShare ?? line.signedShare,
      signedLink: sla ? `/placing/signedlineadvices/${sla._id}` : null,
      status: sla
        ? `Signed ${new Date(raw(sla.receiver?.sentDate) || raw(sla.createdDate)).toLocaleDateString(undefined, { day: "numeric", month: "short" })}`
        : r.status === "ntu"
        ? "Not taken up"
        : r.status === "declined"
        ? "Line declined"
        : latest
        ? "Written · awaiting sign down"
        : "Line requested",
    };
  });

  const placingLines = (records.submissions || [])
    .filter((s) => !mine(s))
    .flatMap((s) =>
      myEntries(s, (records.quoteRequests || []).filter((q) => !mine(q))).map((entry) => {
        const partyId = entry.partyId;
        const ofParty = (list, partyOf) => (list || []).filter((r) => r.submissionId === s._id && partyOf(r) === partyId);
        // Several requests (and quotes) per party: the furthest-along request, the latest quote
        const request = furthestRequest(ofParty((records.quoteRequests || []).filter((q) => !mine(q)), carrierOf.quoteRequest));
        const quote = ofParty((records.quotes || []).filter(mine), carrierOf.quote).sort(
          (a, b) => time(b.stampedDate || b.createdDate) - time(a.stampedDate || a.createdDate)
        )[0];
        const offer = forParty((records.offers || []).filter(mine), s._id, carrierOf.offer, partyId);
        const sla = forParty((records.signedLineAdvices || []).filter((a) => !mine(a)), s._id, carrierOf.signedLineAdvice, partyId);
        const lineRequest = forParty((records.lineRequests || []).filter((r) => !mine(r)), s._id, carrierOf.lineRequest, partyId);

        let status;
        if (sla) status = `Signed ${new Date(raw(sla.createdDate)).toLocaleDateString(undefined, { day: "numeric", month: "short" })}`;
        else if (offer) status = "Written · awaiting sign down";
        else if (request?.status === "nominated") status = "Nominated lead";
        else if (request?.status === "ntu") status = "Not taken up";
        else if (quote) status = "Quoted · awaiting broker";
        else if (request?.status === "declined") status = "Declined to quote";
        else if (request) status = "Quote requested";
        else if (lineRequest) status = `Line requested ${readParticipation(lineRequest).requestedShare ?? ""}%`;
        else if (entry.status === "declined" || entry.responseCode === "decline_to_participate") status = "Declined to participate";
        else if (isInterested(entry)) status = "Interest declared · awaiting broker";
        else status = "Submission received";

        return {
          key: `${s._id}-${partyId}`,
          submissionId: s._id,
          riskName: s.name || "Risk",
          asPartyId: partyId,
          link: request ? `/placing/quoterequests/${request._id}` : `/placing/submissions/${s._id}`,
          role: request?.status === "nominated" || offer ? "Lead" : lineRequest && !request ? "Follower" : "Potential lead",
          quoted: readParticipation(quote).quotedShare,
          written: readParticipation(offer).writtenShare,
          signed: readParticipation(sla).signedShare,
          status,
        };
      })
    )
    // Parties with a line request on it are shown by their line request instead
    .filter((l) => !hasLineRequest(l.submissionId, l.asPartyId));

  return [...orderLines, ...placingLines];
};

/** Start of the current calendar quarter. */
export const quarterStart = (now = new Date()) => new Date(now.getFullYear(), Math.floor(now.getMonth() / 3) * 3, 1);
